import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import { LockReset } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

/**
 * Public page for completing a password reset or accepting an account invite
 * (/reset-password?token=…&welcome=1). Token links are sent by email/WhatsApp.
 */
const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const isWelcome = params.get('welcome') === '1';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validation = useMemo(() => {
    if (!password) return null;
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (confirm && password !== confirm) return 'Passwords do not match';
    return null;
  }, [password, confirm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validation || !password || password !== confirm) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { token, newPassword: password });
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        px: 2,
      }}
    >
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <LockReset sx={{ fontSize: 56, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="h5" fontWeight={800} gutterBottom>
              {isWelcome ? 'Welcome — set your password' : 'Reset your password'}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {isWelcome
                ? 'Choose a password to activate your MES account.'
                : 'Choose a new password for your MES account.'}
            </Typography>
          </Box>

          {!token && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This link is missing its token. Use the link from your email or WhatsApp message,
              or request a new one from the login page.
            </Alert>
          )}

          {done ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Password set. You can now sign in.
              </Alert>
              <Button fullWidth variant="contained" size="large" onClick={() => navigate('/login')}>
                Go to Sign In
              </Button>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField
                fullWidth
                type="password"
                label="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
                disabled={!token || busy}
              />
              <TextField
                fullWidth
                type="password"
                label="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                sx={{ mb: 1 }}
                disabled={!token || busy}
                error={!!validation && !!confirm}
                helperText={confirm ? validation : 'At least 8 characters'}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 2 }}
                disabled={!token || busy || !!validation || !password || !confirm}
              >
                {busy ? <CircularProgress size={24} color="inherit" /> : 'Set Password'}
              </Button>
              <Box textAlign="center" mt={2}>
                <Link component="button" variant="body2" onClick={() => navigate('/login')}>
                  Back to sign in
                </Link>
              </Box>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPassword;
